import * as React from "react"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TablePagination from "@mui/material/TablePagination"
import TableRow from "@mui/material/TableRow"
import { createUseStyles, useTheme } from "react-jss"
import { useAppSelector } from "../../util/hooks"
import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { isqDataSelector } from "./DataMetaDataSlice"
import { IISQTableColumn, IISQTableData } from "../../util/interface"
import { v4 as uuidv4 } from "uuid"
const useStyles = createUseStyles({
  wrapper: {
    width: "100%",
  },
  root: {
    "& .MuiTableCell-head": {
      backgroundColor: "#fbfbfb",
    },
  },
  borderRight: {
    borderRight: "1px solid lightgray",
  },
  borderBottom: {
    borderBottomColor: "transparent !important",
  },
  heading: {
    fontSize: 16,
    fontWeight: 600,
  },
  subHeading: {
    color: "#606060",
    fontSize: 14,
    fontWeight: 600,
  },
})

const colBorder = [0, 1, 2, 3, 4, 5, 6, 7, 8]

const ResultDiv = styled.div`
  height: 25px;
  width: 70px;
  display: inline-block;
  padding: 2px 10px;
  border-radius: 5px;
  margin: 5px;
`

const PassDiv = styled(ResultDiv)`
  background-color: #27af8d;
  color: white;
`

const FailedDiv = styled(ResultDiv)`
  background-color: #fcc;
  font-size: "14px";
  color: #da1818;
`

const columns: IISQTableColumn[] = [
  {
    id: "CHANNEL",
    label: "Channel",
    minWidth: 100,
    align: "left",
  },
  {
    id: "FEATURE",
    label: "Feature",
    minWidth: 170,
    align: "left",
  },
  {
    id: "NULL_COUNT",
    label: "Null Count",
    minWidth: 100,
    align: "center",
  },
  {
    id: "NULL_COUNT_CRITERIA",
    label: "Null Count Criteria",
    minWidth: 200,
    align: "left",
  },
  {
    id: "ZERO_COUNT",
    label: "Zero Count",
    minWidth: 100,
    align: "center",
  },
  {
    id: "ZERO_COUNT_CRITERIA",
    label: "Zero Count Criteria",
    minWidth: 160,
    align: "center",
    format: (value: number) => value.toFixed(0) + " %",
  },
  {
    id: "NULL_COUNT_CHECK",
    label: "Null Count Check",
    minWidth: 130,
    align: "center",
  },
  {
    id: "ZERO_COUNT_CHECK",
    label: "Zero Count Check",
    minWidth: 120,
    align: "center",
  },
  {
    id: "THEORETICAL_RANGE",
    label: "Theoretical Range",
    minWidth: 150,
    align: "center",
  },
  {
    id: "THEORETICAL_RANGE_CHECK",
    label: "Theoritical Range Check",
    minWidth: 160,
    align: "center",
  },
]

function createData({
  CHANNEL,
  FEATURE,
  NULL_COUNT,
  NULL_COUNT_CRITERIA,
  ZERO_COUNT,
  ZERO_COUNT_CRITERIA,
  NULL_COUNT_CHECK,
  ZERO_COUNT_CHECK,
  THEORETICAL_RANGE,
  THEORETICAL_RANGE_CHECK,
}: IISQTableData): IISQTableData {
  return {
    CHANNEL,
    FEATURE,
    NULL_COUNT,
    NULL_COUNT_CRITERIA,
    ZERO_COUNT,
    ZERO_COUNT_CRITERIA,
    NULL_COUNT_CHECK,
    ZERO_COUNT_CHECK,
    THEORETICAL_RANGE,
    THEORETICAL_RANGE_CHECK,
  }
}

const TableHeader: React.FC<{ classes: any }> = ({ classes }) => (
  <TableHead className={classes.root}>
    <TableRow>
      <TableCell
        key={"id"}
        align={"center"}
        className={classes.wrapper + " " + classes.borderRight}
        sx={{ minWidth: 20 }}
      >
        <Typography variant="tableHeader"> S.No </Typography>
      </TableCell>
      {columns.map((column, i) => (
        <TableCell
          key={column.id}
          align={column.align}
          className={classes.wrapper}
          sx={{
            minWidth: column.minWidth,
            borderRight: colBorder.includes(i) ? "1px solid lightgray" : "",
          }}
        >
          <Typography variant="tableHeader"> {column.label} </Typography>
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
)

const formatCellValue = (column: any, value: any, classes: any) => {
  if (column.id === "MATCH_PERCENTAGE") {
    return (
      <div className={classes.matchPercentage}>
        {column.format && typeof value === "number"
          ? column.format(value)
          : value}
      </div>
    )
  }
  if (column.id === "ACCEPTABILITY_CRITERIA") {
    return (
      <div className={classes.acceptabilityCriteria}>
        {column.format && typeof value === "number"
          ? column.format(value)
          : value}
      </div>
    )
  }
  if (column.format && typeof value === "number") {
    return column.format(value)
  }
  if (value === "PASS") {
    return <PassDiv>Pass</PassDiv>
  }
  if (value === "FAIL") {
    return <FailedDiv>Failed</FailedDiv>
  }
  return value
}

const TableCellComponent = ({ column, value, classes, i }: any) => (
  <TableCell
    className={classes.wrapper}
    key={uuidv4()}
    align={column.align}
    sx={{
      borderRight: colBorder.includes(i) ? "1px solid lightgray" : "",
    }}
  >
    {formatCellValue(column, value, classes)}
  </TableCell>
)

const TableRowComponent = ({ row, index, page, rowsPerPage, classes }: any) => (
  <TableRow hover role="checkbox" tabIndex={-1} key={uuidv4()}>
    <TableCell
      align={"left"}
      sx={{ borderRight: "1px solid lightgray", minWidth: 40 }}
    >
      {page * rowsPerPage + index + 1}
    </TableCell>
    {columns.map((column, i) => (
      <TableCellComponent
        key={uuidv4()}
        column={column}
        value={row[column.id]}
        classes={classes}
        i={i}
      />
    ))}
  </TableRow>
)

const TableRows: React.FC<{
  rows: IISQTableData[]
  page: number
  rowsPerPage: number
  classes: any
}> = ({ rows, page, rowsPerPage, classes }) => (
  <TableBody>
    {rows
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((row, index) => (
        <TableRowComponent
          key={uuidv4()}
          row={row}
          index={index}
          page={page}
          rowsPerPage={rowsPerPage}
          classes={classes}
        />
      ))}
  </TableBody>
)

export default function ISQTable() {
  const [page, setPage] = React.useState(0) // Current page in pagination
  const [rowsPerPage, setRowsPerPage] = React.useState(10) // Rows to display per page
  const theme: any = useTheme()
  const classes = useStyles({ theme })

  const data: IISQTableData[] = useAppSelector(isqDataSelector)
  const [rows, setRows] = React.useState<IISQTableData[]>([]) // State to hold transformed rows

  // Transform incoming data into the format required for the table
  React.useEffect(() => {
    const temp: IISQTableData[] = data.map((row) =>
      createData({
        CHANNEL: row["CHANNEL"],
        FEATURE: row["FEATURE"],
        NULL_COUNT: row["NULL_COUNT"],
        NULL_COUNT_CRITERIA: row["NULL_COUNT_CRITERIA"],
        ZERO_COUNT: row["ZERO_COUNT"],
        ZERO_COUNT_CRITERIA: row["ZERO_COUNT_CRITERIA"],
        NULL_COUNT_CHECK: row["NULL_COUNT_CHECK"],
        ZERO_COUNT_CHECK: row["ZERO_COUNT_CHECK"],
        THEORETICAL_RANGE: row["THEORETICAL_RANGE"],
        THEORETICAL_RANGE_CHECK: row["THEORETICAL_RANGE_CHECK"],
      }),
    )
    setRows(temp) // Update the rows state with transformed data
  }, [data])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value) // Update rows per page
    setPage(0) // Reset to the first page
  }

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 515 }}>
        <Table stickyHeader aria-label="sticky table">
          {/* Render table header and rows */}
          <TableHeader classes={classes} />
          <TableRows
            rows={rows}
            page={page}
            rowsPerPage={rowsPerPage}
            classes={classes}
          />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 20]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage} // Function to handle page changes
        onRowsPerPageChange={handleChangeRowsPerPage} // Function to handle changing the number of rows per page
      />
    </Paper>
  )
}
